import { ApolloError, useQuery, useReactiveVar } from '@apollo/client';
import { addMinutes } from 'date-fns';
import { READ_PARK_ZONE_PRICE_ESTIMATES } from 'graphql/queries/readParkZonePriceEstimates';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import { parse } from 'iso8601-duration';
import { isNumber } from 'lodash';
import { useMemo } from 'react';
import { useParkzoneStore } from 'stores/parkzoneStore';
import {
  CountryCode,
  Maybe,
  ParkZonePriceEstimateInput,
  PaymentType,
  PredefinedTicket,
  PriceEstimate,
  Query,
  QueryReadParkZonePriceEstimatesArgs,
} from 'types/generatedSchemaTypes';
import { getCustomerSegmentUid } from 'utils/parkUtils';
import {
  addHoursToDate,
  getTimeFromDateAndLocalize,
  handlePrefixedStartorEndtime,
} from 'utils/timeUtils';

export type DurationSuggestion = {
  duration: number;
  index: number;
  estimatedPrice?: number | 'loading' | 'error';
  serviceFee?: number;
  name?: string;
  type?: string;
  durationType?: string;
  endTime?: string;
  startTime?: string;
  timeUnit?: 'MINUTES' | 'HOURS';
};

export type PricingSuggestion = {
  duration: number;
  type: string | undefined;
  name?: string;
  durationType?: string;
  endTime?: string;
  startTime?: string;
  timeUnit?: 'MINUTES' | 'HOURS';
};

const estimatesInput = (
  parkingZoneUid: string,
  startDate: Date,
  pricingSuggestions?: PricingSuggestion[],
  customerSegmentUid?: string,
  corporateAccountTransactionFee?: number,
  parkingZoneCountryCode?: CountryCode,
  corporateAccountDiscountPercent?: number
): ParkZonePriceEstimateInput[] => {
  const localizedStartDate = handlePrefixedStartorEndtime(
    getTimeFromDateAndLocalize(startDate, parkingZoneCountryCode),
    undefined,
    parkingZoneCountryCode
  );
  return (pricingSuggestions || []).map((item) => ({
    parkingZoneUid,
    startDate: localizedStartDate,
    endDate: setEndDateByType(startDate, item, parkingZoneCountryCode),
    customerSegmentUid,
    ...(corporateAccountTransactionFee != null && {
      serviceFee: corporateAccountTransactionFee,
      paymentType: 'CORPORATE_ACCOUNT' satisfies PaymentType,
    }),
    discountPercent: corporateAccountDiscountPercent,
  }));
};

const setEndDateByType = (
  startDate: Date,
  item: PricingSuggestion,
  parkingZoneCountryCode: CountryCode | undefined
) => {
  if (item?.durationType === 'FIXED_TIME') {
    let endDate = handlePrefixedStartorEndtime(
      item.endTime as string,
      undefined,
      parkingZoneCountryCode
    );
    const prefixedStart = handlePrefixedStartorEndtime(
      item.startTime as string,
      undefined,
      parkingZoneCountryCode
    );
    if (endDate < prefixedStart || endDate === prefixedStart) {
      endDate = new Date(endDate.setDate(endDate.getDate() + 1));
    }
    return endDate;
  }
  if (item.timeUnit === 'MINUTES') {
    return addMinutes(startDate, item.duration);
  }
  if (item.duration < 1) {
    return addMinutes(startDate, item.duration * 60);
  }
  return addHoursToDate(startDate, item.duration);
};

const predefinedDuration = (value: string): number => {
  if (value === null) {
    return 0;
  }
  const { hours, minutes } = parse(value);
  if (hours && !minutes) {
    return hours;
  }
  if (!hours && minutes) {
    return minutes / 60;
  }
  if (hours && minutes) {
    const minutesToHours = minutes / 60;
    const totalHours = hours + minutesToHours;
    return totalHours;
  }
  return 0;
};

const suggestDurations = (
  parkingAllowedFrom: string,
  parkingAllowedUntil: string,
  error?: ApolloError,
  predefinedTickets?: Maybe<PredefinedTicket>[]
): PricingSuggestion[] => {
  if (!parkingAllowedFrom || !parkingAllowedUntil || error) {
    return [];
  }

  const predefinedDurations =
    predefinedTickets
      ?.filter((item) => item?.type === 'FIXED_TIME')
      .map((ticket) => ({
        duration: predefinedDuration(ticket?.duration as string),
        name: ticket?.name as string,
        type: 'PREDEFINED',
        durationType: ticket?.type,
        endTime: ticket?.endTime as string,
        startTime: ticket?.startTime as string,
      })) || [];

  return predefinedDurations;
};

const buildEstimates = (
  durations: PricingSuggestion[],
  estimates: Maybe<Maybe<PriceEstimate>[]> = [],
  estimatesLoading: boolean,
  estimatesError?: ApolloError
): DurationSuggestion[] => {
  const getPrice = (index: number) => {
    if (estimatesLoading) {
      return 'loading';
    }
    if (estimatesError) {
      return 'error';
    }
    const totalAmount = estimates?.[index]?.totalAmount;
    return isNumber(totalAmount) ? totalAmount : undefined;
  };

  const getServiceFee = (index: number) => {
    const serviceFee = estimates?.[index]?.breakdown?.serviceFee?.amount;
    return isNumber(serviceFee) ? serviceFee : undefined;
  };

  return durations.map((item, index) => ({
    duration: item.duration,
    index,
    estimatedPrice: getPrice(index),
    serviceFee: getServiceFee(index),
    type: item.type,
    name: item.name,
    durationType: item.durationType,
    endTime: item.endTime,
    startTime: item.startTime,
    timeUnit: item.timeUnit,
  }));
};

const useSuggestedPricingEstimates = (
  parkingZoneUid: string,
  startDate: Date,
  corporateAccounTransactionFee?: number,
  parkingZoneCountryCode?: CountryCode,
  corporateAccountDiscountPercent?: number
) => {
  const {
    parkingAllowedFrom,
    parkingAllowedUntil,
    pricingInfoError,
    predefinedTickets,
  } = useParkingPricingInfo(parkingZoneUid, true);

  const durations = useMemo(
    () =>
      suggestDurations(
        parkingAllowedFrom,
        parkingAllowedUntil,
        pricingInfoError,
        predefinedTickets.filter((ticket) => ticket?.sessionType !== 'PREPAID')
      ),
    [
      parkingAllowedFrom,
      parkingAllowedUntil,
      pricingInfoError,
      predefinedTickets,
    ]
  );

  const selectedCustomerSegment = useParkzoneStore(
    (state) => state.selectedTariffId
  );

  const customerSegmentUid = getCustomerSegmentUid(selectedCustomerSegment);

  const {
    data: estimatesData,
    loading: estimatesLoading,
    error: estimatesError,
  } = useQuery<Query, QueryReadParkZonePriceEstimatesArgs>(
    READ_PARK_ZONE_PRICE_ESTIMATES,
    {
      variables: {
        input: estimatesInput(
          parkingZoneUid,
          startDate,
          durations,
          customerSegmentUid,
          corporateAccounTransactionFee,
          parkingZoneCountryCode,
          corporateAccountDiscountPercent
        ),
      },
      skip: !parkingZoneUid && !!durations,
    }
  );

  return useMemo(() => {
    if (durations === undefined) {
      return [];
    }
    return buildEstimates(
      durations,
      estimatesData?.readParkZonePriceEstimates,
      estimatesLoading,
      estimatesError
    );
  }, [
    estimatesData?.readParkZonePriceEstimates,
    durations,
    estimatesLoading,
    estimatesError,
  ]);
};

export default useSuggestedPricingEstimates;
